import { useCallback, useEffect, useRef, useState } from "react";

const msg = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

/**
 * Infinite scroll over a server-paginated list.
 *
 * `fetchPage(page)` must resolve to `{ items, pagination: { total, pages }, ...extra }`.
 * Pages are appended as the sentinel (`sentinelRef`) scrolls into view. Changing
 * `resetKey` (filters) starts over from page 1. `reload()` re-fetches every page
 * loaded so far, so live updates keep the whole list fresh without losing scroll.
 */
export default function useInfiniteList(fetchPage, resetKey, { debounceMs = 250 } = {}) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1, extra: {} });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const loadedPages = useRef(0);
  const seq = useRef(0); // ignores responses from superseded requests
  const busy = useRef(false);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const split = (res) => {
    const { items: list, pagination, ...extra } = res;
    return { list, total: pagination?.total ?? list.length, pages: pagination?.pages ?? 1, extra };
  };

  const dedupe = (list) => {
    const seen = new Set();
    return list.filter((i) => (seen.has(i.id) ? false : seen.add(i.id)));
  };

  const loadFirst = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    try {
      const res = split(await fetchRef.current(1));
      if (id !== seq.current) return;
      setItems(res.list);
      setMeta({ total: res.total, pages: res.pages, extra: res.extra });
      loadedPages.current = 1;
      setError("");
    } catch (e) {
      if (id === seq.current) setError(msg(e, "Could not load"));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (busy.current || loadedPages.current === 0 || loadedPages.current >= meta.pages) return;
    busy.current = true;
    const id = seq.current;
    setLoadingMore(true);
    try {
      const res = split(await fetchRef.current(loadedPages.current + 1));
      if (id !== seq.current) return;
      setItems((prev) => dedupe([...prev, ...res.list]));
      setMeta({ total: res.total, pages: res.pages, extra: res.extra });
      loadedPages.current += 1;
    } catch (e) {
      if (id === seq.current) setError(msg(e, "Could not load more"));
    } finally {
      busy.current = false;
      if (id === seq.current) setLoadingMore(false);
    }
  }, [meta.pages]);

  /** Re-fetch every page loaded so far (live updates). */
  const reload = useCallback(async () => {
    const count = Math.max(loadedPages.current, 1);
    const id = ++seq.current;
    try {
      const results = await Promise.all(Array.from({ length: count }, (_, i) => fetchRef.current(i + 1)));
      if (id !== seq.current) return;
      const parts = results.map(split);
      setItems(dedupe(parts.flatMap((p) => p.list)));
      const last = parts[parts.length - 1];
      setMeta({ total: last.total, pages: last.pages, extra: last.extra });
      loadedPages.current = Math.min(count, last.pages) || 1;
      setError("");
    } catch (e) {
      if (id === seq.current) setError(msg(e, "Could not refresh"));
    }
  }, []);

  // Start over whenever the filters change (debounced).
  useEffect(() => {
    loadedPages.current = 0;
    const t = setTimeout(loadFirst, debounceMs);
    return () => clearTimeout(t);
  }, [resetKey, loadFirst, debounceMs]);

  // Sentinel element at the end of the list: when visible, fetch the next page.
  const observer = useRef(null);
  const sentinelRef = useCallback(
    (node) => {
      observer.current?.disconnect();
      if (!node || typeof IntersectionObserver === "undefined") return;
      observer.current = new IntersectionObserver((entries) => entries[0].isIntersecting && loadMore(), {
        rootMargin: "400px",
      });
      observer.current.observe(node);
    },
    [loadMore],
  );

  return {
    items,
    setItems,
    total: meta.total,
    extra: meta.extra,
    loading,
    loadingMore,
    error,
    hasMore: loadedPages.current < meta.pages,
    loadMore,
    reload,
    retry: loadFirst,
    sentinelRef,
  };
}
